import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BiSearch } from "react-icons/bi";
import { usePagination, useTable } from "react-table";
import Loader from "../../components/Loader";
import Api from "../../service/http";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function userInitials(row) {
  const a = (row.original.first_name || "").trim().charAt(0);
  const b = (row.original.last_name || "").trim().charAt(0);
  const s = (a + b).toUpperCase();
  return s || "?";
}

/** Matches API: users.login_type (1 = signup, 2 = facebook, 3 = google) */
function loginTypeLabel(loginType) {
  const n = Number(loginType);
  if (n === 2) return "Facebook";
  if (n === 3) return "Google";
  if (n === 1) return "Sign up";
  return "—";
}

function subscriptionPeriodLabel(period) {
  const p = Number(period);
  return p === 1 ? "Monthly" : p === 2 ? "Yearly" : "—";
}

function normalizeStatus(status) {
  if (status == null || status === "") return "";
  return String(status).trim().toLowerCase();
}

function statusBadgeClass(status) {
  const s = normalizeStatus(status);
  if (s === "active" || s === "trialing") return "bg-emerald-100 text-emerald-800";
  if (s === "canceled" || s === "cancelled") return "bg-amber-100 text-amber-800";
  if (s) return "bg-slate-100 text-slate-700";
  return "bg-gray-100 text-gray-600";
}

const Users = () => {
  const [users, setUsers] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const fetchUsers = async (searchTerm = "", page = 1) => {
    setLoading(true);
    try {
      const response = await Api.get(`web/users?page=${page}&search=${encodeURIComponent(searchTerm)}`);
      const data = response.data;
      setUsers(data.data || []);
      setPageCount(data.last_page || 0);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(
    debounce((value, page) => {
      fetchUsers(value, page);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedFetch(search, pagination.pageIndex + 1);
  }, [search, pagination.pageIndex]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const columns = useMemo(
    () => [
      {
        Header: "Photo",
        accessor: "dp",
        Cell: ({ value, row }) =>
          value ? (
            <img
              src={value}
              alt=""
              className="h-10 w-10 rounded-full object-cover border border-gray-100 bg-gray-50"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold border border-indigo-200/60">
              {userInitials(row)}
            </div>
          ),
      },
      {
        Header: "User",
        id: "contact",
        accessor: (row) =>
          [row.first_name, row.last_name].filter(Boolean).join(" ").trim() || "—",
        Cell: ({ value, row }) => {
          const email = row.original.email || "—";
          const phone = row.original.phone || "—";
          return (
            <div className="flex flex-col gap-1 max-w-xs">
              <span className="font-semibold text-gray-900">{value}</span>
              <span className="text-gray-700 text-sm">{email}</span>
              <span className="text-xs text-gray-500">{phone}</span>
            </div>
          );
        },
      },
      {
        Header: "Login",
        accessor: "login_type",
        Cell: ({ value }) => (
          <span className="text-gray-600">{loginTypeLabel(value)}</span>
        ),
      },
      {
        Header: "Joined",
        accessor: "created_at",
        Cell: ({ value }) => <span className="text-gray-600">{formatDate(value)}</span>,
      },
      {
        Header: "Subscription",
        id: "subscription",
        accessor: (row) => row.expired_at,
        Cell: ({ row }) => {
          const sub = row.original.active_subscription;
          if (sub) {
            const endRaw =
              sub.status && normalizeStatus(sub.status) === "trialing"
                ? sub.trial_ends_at
                : sub.current_period_end;
            return (
              <div className="flex flex-col gap-1 max-w-xs">
                <span
                  className={`inline-flex w-fit px-2 py-0.5 rounded-md text-xs font-semibold ${statusBadgeClass(sub.status)}`}
                >
                  {sub.status != null && sub.status !== "" ? String(sub.status) : "—"}
                </span>
                <span className="text-gray-600 text-sm">
                  {subscriptionPeriodLabel(sub.period)}
                  {sub.provider ? (
                    <span className="text-gray-400"> · {sub.provider}</span>
                  ) : null}
                </span>
                <span className="text-xs text-gray-500">Ends {formatDate(endRaw)}</span>
              </div>
            );
          }
          const exp = row.original.expired_at;
          const gw = row.original.subs_gateway;
          return (
            <div className="flex flex-col gap-0.5 max-w-xs">
              <span className="text-gray-600">{formatDate(exp)}</span>
              {gw ? <span className="text-xs text-gray-400">{gw}</span> : null}
              {!exp && !gw ? (
                <span className="text-xs text-gray-400">No subscription on file</span>
              ) : null}
            </div>
          );
        },
      },
      {
        Header: "Role",
        accessor: "is_admin",
        Cell: ({ value }) =>
          value ? (
            <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-semibold bg-amber-100 text-amber-800">
              Admin
            </span>
          ) : (
            <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
              User
            </span>
          ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    state: tableState,
  } = useTable(
    {
      columns,
      data: users,
      manualPagination: true,
      pageCount,
      initialState: { pageIndex: pagination.pageIndex },
    },
    usePagination
  );

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: tableState.pageIndex }));
  }, [tableState.pageIndex]);

  return (
    <div className="p-0 h-full overflow-y-auto scrollbar-hide bg-gray-50/50">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Users</h1>
            <p className="text-gray-500 text-sm mt-1">View registered users and subscription status.</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50/30">
            <div className="relative max-w-md">
              <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table {...getTableProps()} className="w-full">
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()} className="bg-gray-50/50">
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps()}
                        className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100"
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className="py-20">
                      <Loader size="lg" className="mx-auto" />
                    </td>
                  </tr>
                ) : page.length > 0 ? (
                  page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} className="hover:bg-gray-50/50 transition-colors">
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()} className="px-6 py-4 text-sm text-gray-600 align-middle">
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                          <BiSearch className="text-2xl text-gray-300" />
                        </div>
                        <p>No users found matching your criteria</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/30">
            <div className="text-sm text-gray-500 font-medium">
              Showing page <span className="text-gray-900">{pagination.pageIndex + 1}</span> of{" "}
              <span className="text-gray-900">{Math.max(pageCount, 1)}</span>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => nextPage()}
                disabled={!canNextPage}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
